import React, { useState, useMemo } from 'react';
import { Switch, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import styles from 'components/admin/businesses/FeatureFlags.module.scss';
import { useSaveFeatureFlagValue } from 'components/admin/businesses/LaundroworksIntegration/useSaveFeatureFlagValue';
import CentsLoader from 'components/common/centsLoader/CentsLoader.jsx';
import { useGetFeatureFlagsForBusiness } from 'api/queries/useGetFeatureFlagsForBusiness';
import {
  useGetLaundroworksActiveJobs,
  useAddLaundroworksCustomerSyncJob,
  JobScope,
} from 'api/queries/useLaundroworksCustomerSyncJobs';
import { useLaundroworksSettings } from 'api/queries/useLaundroworksSettings';
import {
  FEATURE_FLAG_LAUNDROWORKS_CUSTOMER_SYNC,
  FEATURE_FLAG_LAUNDROWORKS_INTEGRATION,
  FEATURE_FLAG_LAUNDRYCAT_CUSTOMER_SYNC,
} from 'constants/constants';
import { ReactComponent as CheckCircleIcon } from 'assets/images/icon_check_circle.svg';
import { ReactComponent as XCircleIcon } from 'assets/images/icon_x_circle.svg';
import {
  useAddLaundryCatCustomerSyncJob,
  useGetLaundryCatActiveJobs,
} from '../../../../api/queries/useLaundrycatCustomerSyncJobs';
import integrationStyles from './LaundroworksIntegration.module.scss';

interface LaundroworksIntegrationProps {
  businessId: number;
  businessName: string;
}

export function LaundroworksIntegration({
  businessId,
  businessName,
}: LaundroworksIntegrationProps) {
  const [error, setError] = useState<string | null>(null);
  const [localFlagStates, setLocalFlagStates] = useState<Record<string, boolean>>({});

  const saveFeatureFlagValue = useSaveFeatureFlagValue({
    setError,
    setLocalFlagStates,
  });

  const { data: featureFlags, isLoading: isLoadingFeatureFlags } = useGetFeatureFlagsForBusiness({
    businessId,
  });

  const { data: laundroworksSettings, isLoading: isLoadingLaundroworksSettings } =
    useLaundroworksSettings(businessId);

  const {
    data: activeLWJobs,
    isLoading: isLoadingLWActiveJobs,
    refetch: refetchLWActiveJobs,
  } = useGetLaundroworksActiveJobs(businessId);

  const {
    data: activeLCJobs,
    isLoading: isLoadingLCActiveJobs,
    refetch: refetchLCActiveJobs,
  } = useGetLaundryCatActiveJobs(businessId);

  const addLWSyncJob = useAddLaundroworksCustomerSyncJob();
  const addLCSyncJob = useAddLaundryCatCustomerSyncJob();

  const incrementalLWJobs = activeLWJobs?.filter((job) => job.scope === JobScope.INCREMENTAL);
  const fullLWJobs = activeLWJobs?.filter((job) => job.scope === JobScope.FULL);

  const incrementalLCJobs = activeLCJobs?.filter((job) => job.scope === JobScope.INCREMENTAL);
  const fullLCJobs = activeLCJobs?.filter((job) => job.scope === JobScope.FULL);

  // Get the actual state values from the flags
  const lwIntegrationFlag = featureFlags?.find(
    (flag) => flag.key === FEATURE_FLAG_LAUNDROWORKS_INTEGRATION,
  );
  const lwCustomerSyncFlag = featureFlags?.find(
    (flag) => flag.key === FEATURE_FLAG_LAUNDROWORKS_CUSTOMER_SYNC,
  );

  const lcCustomerSyncFlag = featureFlags?.find(
    (flag) => flag.key === FEATURE_FLAG_LAUNDRYCAT_CUSTOMER_SYNC,
  );

  const handleToggleFlag = (flagKey, currentState) => {
    const newValue = !currentState;
    setLocalFlagStates((prev) => ({
      ...prev,
      [flagKey]: newValue,
    }));
    saveFeatureFlagValue.mutate({
      businessId,
      flagKey,
      value: newValue,
    });
  };

  const startLWSync = (scope: JobScope) => {
    addLWSyncJob.mutate(
      { businessId, scope },
      {
        onSuccess: () => {
          refetchLWActiveJobs();
        },
      },
    );
  };

  const startLCSync = (scope: JobScope) => {
    addLCSyncJob.mutate(
      { businessId, scope },
      {
        onSuccess: () => {
          refetchLCActiveJobs();
        },
      },
    );
  };

  // Use local state if present, otherwise use server state
  const lwCustomerSyncChecked =
    localFlagStates[FEATURE_FLAG_LAUNDROWORKS_CUSTOMER_SYNC] !== undefined
      ? localFlagStates[FEATURE_FLAG_LAUNDROWORKS_CUSTOMER_SYNC]
      : lwCustomerSyncFlag?.state || false;

  const lcCustomerSyncChecked =
    localFlagStates[FEATURE_FLAG_LAUNDRYCAT_CUSTOMER_SYNC] !== undefined
      ? localFlagStates[FEATURE_FLAG_LAUNDRYCAT_CUSTOMER_SYNC]
      : lcCustomerSyncFlag?.state || false;

  const lwIntegrationChecked =
    localFlagStates[FEATURE_FLAG_LAUNDROWORKS_INTEGRATION] !== undefined
      ? localFlagStates[FEATURE_FLAG_LAUNDROWORKS_INTEGRATION]
      : lwIntegrationFlag?.state || false;

  // Check if any store has an access token provided
  const isProperlyConfigured = useMemo(() => {
    if (!laundroworksSettings) return false;

    // Iterate over all store settings and check if any has an accessToken
    return Object.values(laundroworksSettings).some(
      (settings) =>
        settings.customerKey &&
        settings.customerKey.length > 0 &&
        settings.laundroworksLocationId &&
        settings.laundroworksLocationId.length > 0,
    );
  }, [laundroworksSettings]);

  return (
    <section className={styles.flagSection}>
      <h5 className="mb-4">Laundroworks Integration for {businessName}</h5>
      {error && <p className={styles.error}>{error}</p>}

      <Grid container rowSpacing={1} alignItems="center" spacing={2} className="w-100 mb-2">
        <Grid size={{ xs: 5, md: 4 }}>
          <Typography className={styles.flagLabel}>Enable Laundroworks Customer Sync</Typography>
        </Grid>
        <Grid size="grow">
          <Typography variant="body2">
            Enable feature flag to sync customers from Laundroworks into this business
          </Typography>
        </Grid>
        <Grid size={2}>
          {isLoadingFeatureFlags ? (
            <CentsLoader />
          ) : (
            <Switch
              checked={lwCustomerSyncChecked}
              onChange={() =>
                handleToggleFlag(FEATURE_FLAG_LAUNDROWORKS_CUSTOMER_SYNC, lwCustomerSyncChecked)
              }
              color="primary"
            />
          )}
        </Grid>
      </Grid>
      <Grid container rowSpacing={1} alignItems="center" spacing={2} className="w-100 mb-2" gap={2}>
        <Grid size={{ xs: 5, md: 4 }}>
          <Typography className={styles.flagLabel}>Enable Laundroworks Integration</Typography>
        </Grid>
        <Grid size="grow">
          <Typography variant="body2">
            Enable feature flag to allow Customer filtering based on Laundry User cards
          </Typography>
        </Grid>
        <Grid size={2}>
          {isLoadingFeatureFlags ? (
            <CentsLoader />
          ) : (
            <Switch
              checked={lwIntegrationChecked}
              onChange={() =>
                handleToggleFlag(FEATURE_FLAG_LAUNDROWORKS_INTEGRATION, lwIntegrationChecked)
              }
              color="primary"
            />
          )}
        </Grid>
      </Grid>

      <Grid container rowSpacing={1} alignItems="center" spacing={2} className="w-100 mb-2">
        <Grid size={{ xs: 5, md: 4 }}>
          <Typography className={styles.flagLabel}>Enable LaundryCat Customer Sync</Typography>
        </Grid>
        <Grid size="grow">
          <Typography variant="body2">
            Enable feature flag to sync customers from LaundryCat(Firebase) into this business
          </Typography>
        </Grid>
        <Grid size={2}>
          {isLoadingFeatureFlags ? (
            <CentsLoader />
          ) : (
            <Switch
              checked={lcCustomerSyncChecked}
              onChange={() =>
                handleToggleFlag(FEATURE_FLAG_LAUNDRYCAT_CUSTOMER_SYNC, lcCustomerSyncChecked)
              }
              color="primary"
            />
          )}
        </Grid>
      </Grid>

      <Grid container rowSpacing={1} alignItems="center" spacing={2} className="w-100 mb-2" gap={2}>
        <Grid size={{ xs: 5, md: 4 }}>
          <Typography className={styles.flagLabel}>Laundroworks Settings</Typography>
        </Grid>
        <Grid size="grow">
          <Typography variant="body2">
            Integration to Laundroworks API has been properly configured for at least one store.
          </Typography>
        </Grid>
        <Grid size={2}>
          {isLoadingLaundroworksSettings ? (
            <CentsLoader />
          ) : isProperlyConfigured ? (
            <CheckCircleIcon className={integrationStyles.statusIcon} />
          ) : (
            <XCircleIcon className={integrationStyles.statusIcon} />
          )}
        </Grid>
      </Grid>

      <Grid container rowSpacing={1} alignItems="center" spacing={2} className="w-100 mb-2">
        <Grid size={{ xs: 5, md: 4 }}>
          <Typography className={styles.flagLabel}>Laundroworks Full sync</Typography>
        </Grid>
        <Grid size="grow">
          <Typography variant="body2">Queue a one-time full sync of Laundroworks data</Typography>
        </Grid>
        <Grid size={2}>
          {isLoadingLWActiveJobs && <CentsLoader />}
          {!isLoadingLWActiveJobs && !!fullLWJobs?.length && (
            <Typography variant="body2">
              Running {fullLWJobs.length} {fullLWJobs.length === 1 ? 'job' : 'jobs'}
            </Typography>
          )}
          {!isLoadingLWActiveJobs && fullLWJobs?.length === 0 && (
            <button
              onClick={() => startLWSync(JobScope.FULL)}
              disabled={addLWSyncJob.isLoading}
              color="primary"
            >
              Sync Now
            </button>
          )}
        </Grid>
      </Grid>
      <Grid container rowSpacing={1} alignItems="center" spacing={2} className="w-100 mb-2">
        <Grid size={{ xs: 5, md: 4 }}>
          <Typography className={styles.flagLabel}>Laundroworks Incremental Sync</Typography>
        </Grid>
        <Grid size="grow">
          <Typography variant="body2">
            Queue an incremental sync job of Laundroworks data
          </Typography>
        </Grid>
        <Grid size={2}>
          {isLoadingLWActiveJobs && <CentsLoader />}
          {!isLoadingLWActiveJobs && incrementalLWJobs && incrementalLWJobs.length > 0 && (
            <Typography variant="body2">Running {incrementalLWJobs.length} jobs</Typography>
          )}
          {!isLoadingLWActiveJobs && incrementalLWJobs?.length === 0 && (
            <button
              onClick={() => startLWSync(JobScope.INCREMENTAL)}
              disabled={addLWSyncJob.isLoading}
              color="primary"
            >
              Schedule
            </button>
          )}
        </Grid>
      </Grid>

      <Grid container rowSpacing={1} alignItems="center" spacing={2} className="w-100 mb-2">
        <Grid size={{ xs: 5, md: 4 }}>
          <Typography className={styles.flagLabel}>LaundryCat Full sync</Typography>
        </Grid>
        <Grid size="grow">
          <Typography variant="body2">Queue a one-time full sync of LaundryCat data</Typography>
        </Grid>
        <Grid size={2}>
          {isLoadingLCActiveJobs && <CentsLoader />}
          {!isLoadingLCActiveJobs && !!fullLCJobs?.length && (
            <Typography variant="body2">
              Running {fullLCJobs.length} {fullLCJobs.length === 1 ? 'job' : 'jobs'}
            </Typography>
          )}
          {!isLoadingLCActiveJobs && fullLCJobs?.length === 0 && (
            <button
              onClick={() => startLCSync(JobScope.FULL)}
              disabled={addLCSyncJob.isLoading}
              color="primary"
            >
              Sync Now
            </button>
          )}
        </Grid>
      </Grid>
      <Grid container rowSpacing={1} alignItems="center" spacing={2} className="w-100 mb-2">
        <Grid size={{ xs: 5, md: 4 }}>
          <Typography className={styles.flagLabel}>LaundryCat Incremental Sync</Typography>
        </Grid>
        <Grid size="grow">
          <Typography variant="body2">Queue an incremental sync job of LaundryCat data</Typography>
        </Grid>
        <Grid size={2}>
          {isLoadingLCActiveJobs && <CentsLoader />}
          {!isLoadingLCActiveJobs && incrementalLCJobs && incrementalLCJobs.length > 0 && (
            <Typography variant="body2">Running {incrementalLCJobs.length} jobs</Typography>
          )}
          {!isLoadingLCActiveJobs && incrementalLCJobs?.length === 0 && (
            <button
              onClick={() => startLCSync(JobScope.INCREMENTAL)}
              disabled={addLCSyncJob.isLoading}
              color="primary"
            >
              Schedule
            </button>
          )}
        </Grid>
      </Grid>
    </section>
  );
}
